import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from 'components/AppIcon';

const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    badge: '30-Day Free Trial',
    price: 0,
    priceLabel: 'Free',
    billingNote: 'No credit card required',
    clientLimit: 5,
    stripePriceId: import.meta.env?.VITE_STRIPE_PRICE_STARTER || 'price_trial_id',
    color: 'from-slate-600 to-slate-700',
    accentColor: 'text-slate-400',
    borderColor: 'border-slate-700/60',
    features: [
      { text: 'Up to 5 active clients', icon: 'Users' },
      { text: 'Workout Builder', icon: 'Dumbbell' },
      { text: 'Client Plan Builder', icon: 'CalendarPlus' },
      { text: 'Nutrition Plan Builder', icon: 'Apple' },
      { text: 'Client Messaging', icon: 'MessageCircle' },
      { text: 'Progress Tracking', icon: 'TrendingUp' },
    ],
    cta: 'Start Free Trial',
    ctaStyle: 'bg-slate-700 hover:bg-slate-600 text-white border border-slate-600',
  },
  {
    id: 'pro',
    name: 'Pro',
    badge: 'Most Popular',
    price: 49,
    priceLabel: '$49',
    billingNote: 'per month, billed monthly',
    clientLimit: 20,
    stripePriceId: import.meta.env?.VITE_STRIPE_PRICE_PRO || 'price_pro_id',
    color: 'from-primary to-primary/80',
    accentColor: 'text-primary',
    borderColor: 'border-primary/40',
    features: [
      { text: 'Up to 20 active clients', icon: 'Users' },
      { text: 'Everything in Starter', icon: 'CheckCircle' },
      { text: 'Custom Branding', icon: 'Palette' },
      { text: 'Predictive Analytics', icon: 'BarChart3' },
      { text: 'Wearable Device Sync', icon: 'Watch' },
      { text: 'Priority Support', icon: 'Headphones' },
    ],
    cta: 'Choose Pro',
    ctaStyle: 'wellness-gradient-primary text-white shadow-lg hover:opacity-90',
  },
];

const PricingPage = () => {
  const navigate = useNavigate();
  const [loadingPlan, setLoadingPlan] = useState(null);

  const handleSelectPlan = async (plan) => {
    setLoadingPlan(plan?.id);

    // If Stripe key is not configured, navigate to checkout page with plan info
    // The checkout page will handle the Stripe redirect when keys are available
    setTimeout(() => {
      navigate(`/checkout?plan=${plan?.id}&priceId=${plan?.stripePriceId}`);
      setLoadingPlan(null);
    }, 400);
  };

  return (
    <div className="min-h-screen" style={{ background: '#0A0A0B', color: '#E8EAED' }}>
      {/* Header */}
      <div style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)', backdropFilter: 'blur(20px)' }}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/login')} className="flex items-center space-x-2 transition-colors" style={{ color: '#6B7280' }}>
            <div className="flex items-center justify-center w-8 h-8 rounded-lg" style={{ background: 'linear-gradient(135deg, #FF6B00, #FF8C35)' }}>
              <Icon name="Zap" size={16} color="white" />
            </div>
            <span className="font-bold text-lg" style={{ fontFamily: "'Inter Tight', sans-serif", color: '#E8EAED' }}>FormForge</span>
          </button>
          <button onClick={() => navigate('/login')} className="text-sm transition-colors" style={{ color: '#6B7280' }}>
            Already have an account? <span style={{ color: '#FF6B00' }} className="font-medium">Sign in</span>
          </button>
        </div>
      </div>
      <div className="max-w-5xl mx-auto px-6 py-16">
        {/* Hero */}
        <motion.div className="text-center mb-14" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="inline-flex items-center space-x-2 px-4 py-1.5 rounded-full text-sm font-medium mb-6" style={{ background: 'rgba(255,107,0,0.1)', color: '#FF6B00', border: '1px solid rgba(255,107,0,0.2)' }}>
            <Icon name="Zap" size={14} />
            <span>Simple, transparent pricing</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight" style={{ fontFamily: "'Inter Tight', sans-serif", color: '#E8EAED' }}>Choose your plan</h1>
          <p className="text-lg max-w-xl mx-auto" style={{ color: '#6B7280' }}>Start free for 30 days. No credit card needed. Upgrade when you're ready to grow your client base.</p>
        </motion.div>

        {/* Plan Cards */}
        <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {PLANS?.map((plan, i) => (
            <motion.div
              key={plan?.id}
              className="relative rounded-2xl p-8 flex flex-col"
              style={plan?.id === 'pro' ? {
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,107,0,0.3)',
                backdropFilter: 'blur(20px)',
                boxShadow: '0 0 30px rgba(255,107,0,0.1)'
              } : {
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                backdropFilter: 'blur(20px)'
              }}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
            >
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold" style={{ fontFamily: "'Inter Tight', sans-serif", color: '#E8EAED' }}>{plan?.name}</h2>
                  <p className="text-sm mt-0.5" style={{ color: '#6B7280' }}>{plan?.billingNote}</p>
                </div>
                <span className="text-xs font-semibold px-3 py-1 rounded-full" style={plan?.id === 'pro' ? { background: 'rgba(255,107,0,0.15)', color: '#FF6B00' } : { background: 'rgba(255,255,255,0.06)', color: '#6B7280' }}>
                  {plan?.badge}
                </span>
              </div>

              <div className="mb-6">
                <div className="flex items-end gap-1">
                  <span className="text-5xl font-extrabold" style={{ fontFamily: "'Inter Tight', sans-serif", color: '#E8EAED' }}>{plan?.priceLabel}</span>
                  {plan?.price > 0 && <span className="mb-2 text-sm" style={{ color: '#6B7280' }}>/mo</span>}
                </div>
                <p className="text-sm font-medium mt-1" style={{ color: plan?.id === 'pro' ? '#FF6B00' : '#6B7280' }}>Up to {plan?.clientLimit} active clients</p>
              </div>

              <ul className="space-y-3 mb-8 flex-1">
                {plan?.features?.map((feature) => (
                  <li key={feature?.text} className="flex items-center space-x-3">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center" style={plan?.id === 'pro' ? { background: 'rgba(255,107,0,0.15)' } : { background: 'rgba(255,255,255,0.06)' }}>
                      <Icon name={feature?.icon} size={11} style={{ color: plan?.id === 'pro' ? '#FF6B00' : '#6B7280' }} />
                    </div>
                    <span className="text-sm" style={{ color: '#E8EAED' }}>{feature?.text}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleSelectPlan(plan)}
                disabled={loadingPlan === plan?.id}
                className="w-full py-3 rounded-xl font-semibold text-sm transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-60"
                style={plan?.id === 'pro' ? {
                  background: 'linear-gradient(135deg, #FF6B00, #FF8C35)',
                  color: '#fff',
                  boxShadow: '0 0 20px rgba(255,107,0,0.3)'
                } : {
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  color: '#E8EAED'
                }}
              >
                {loadingPlan === plan?.id ? (
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <span>{plan?.cta}</span>
                    <Icon name="ArrowRight" size={16} />
                  </>
                )}
              </button>
            </motion.div>
          ))}
        </div>

        {/* Trust signals */}
        <motion.div className="mt-14 text-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm" style={{ color: '#6B7280' }}>
            <div className="flex items-center space-x-2">
              <Icon name="Shield" size={16} style={{ color: '#AAFF00' }} />
              <span>Secure payments via Stripe</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="RefreshCw" size={16} style={{ color: '#00D4FF' }} />
              <span>Cancel anytime</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Lock" size={16} style={{ color: '#FF6B00' }} />
              <span>SSL encrypted</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PricingPage;
